/**
 * Notizen eines Dokuments (pro Nutzer): Vektor-Modell, Undo, Autosave mit
 * Versionsprüfung (If-Match) und Zusammenführen bei parallelen Änderungen.
 */
import type { AnnotationPreset, StrokeDocument, StrokeDocumentInput } from "@piano/shared";
import { ApiError } from "../api/client.js";
import { api } from "../api/index.js";
import { ui } from "../stores/ui.svelte.js";
import {
  HIGHLIGHT_ALPHA,
  INK_COLORS,
  MIN_POINT_DISTANCE,
  newStrokeId,
  strokeSize,
  type DraftStroke,
  type Point,
  type Tool,
} from "./ink.js";

type UndoEntry =
  { type: "stroke"; page: number; id: string } | { type: "clear"; page: number; strokes: DraftStroke[] };

const SAVE_DELAY = 1200;
const RETRY_DELAY = 5000;
const KEEPALIVE_MAX_BYTES = 60 * 1024;
const MAX_UNDO = 100;

export class InkStore {
  tool = $state<Tool>("pen");
  color = $state<string>(INK_COLORS[1].value);
  size = $state(3);
  /** Erhöht sich bei jeder Änderung einer Seite → Seiten zeichnen neu. */
  revisions = $state<Record<number, number>>({});
  canUndo = $state(false);
  saving = $state<"idle" | "pending" | "saving" | "error">("idle");

  private pages = new Map<number, DraftStroke[]>();
  private undoStack: UndoEntry[] = [];
  private version = 0;
  private loaded = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inflight: Promise<void> | null = null;
  private disposed = false;

  constructor(private readonly documentId: string) {}

  applyPreset(preset: AnnotationPreset | null): void {
    if (!preset) return;
    this.tool = preset.tool;
    this.color = preset.color.length === 7 ? preset.color.toUpperCase() : this.color;
    this.size = preset.size;
  }

  get preset(): AnnotationPreset {
    return { tool: this.tool === "highlighter" ? "highlighter" : "pen", color: this.color, size: this.size };
  }

  async load(): Promise<void> {
    const dto = await api.annotations(this.documentId);
    if (this.disposed) return;
    this.replace(dto.document, dto.version);
    this.loaded = true;
  }

  /** Neu laden, wenn ein anderes Gerät gespeichert hat und hier nichts offen ist. */
  async refreshIfClean(): Promise<void> {
    if (this.saving !== "idle" || this.timer) return;
    await this.load().catch(() => undefined);
  }

  strokes(page: number): readonly DraftStroke[] {
    return this.pages.get(page) ?? [];
  }

  hasInk(page: number): boolean {
    void this.revisions[page]; // reaktiv: ändert sich mit jeder Änderung der Seite
    return (this.pages.get(page)?.length ?? 0) > 0;
  }

  // --- Zeichnen -------------------------------------------------------------

  begin(page: number, point: Point, displayWidth: number, pressure: boolean): DraftStroke {
    const erase = this.tool === "eraser";
    return {
      id: newStrokeId(),
      tool: this.tool,
      mode: erase ? "erase" : "draw",
      color: erase ? "#000000" : this.color,
      alpha: this.tool === "highlighter" ? HIGHLIGHT_ALPHA : 1,
      size: strokeSize(this.tool, this.size, displayWidth),
      hasPressure: pressure && !erase,
      points: [point],
    };
  }

  /** Fügt Punkte hinzu, sehr nahe Punkte werden verworfen. Liefert true bei Änderung. */
  extend(stroke: DraftStroke, points: Point[]): boolean {
    let changed = false;
    for (const point of points) {
      const last = stroke.points[stroke.points.length - 1]!;
      if (Math.hypot(point[0] - last[0], point[1] - last[1]) < MIN_POINT_DISTANCE) continue;
      if (stroke.points.length >= 20_000) break;
      stroke.points.push(point);
      changed = true;
    }
    return changed;
  }

  commit(page: number, stroke: DraftStroke): void {
    // Radieren auf leerer Seite ist sinnlos
    if (stroke.mode === "erase" && !this.hasInk(page)) return;
    const list = this.pages.get(page) ?? [];
    list.push(stroke);
    this.pages.set(page, list);
    this.pushUndo({ type: "stroke", page, id: stroke.id });
    this.touch(page);
  }

  undo(): void {
    const entry = this.undoStack.pop();
    this.canUndo = this.undoStack.length > 0;
    if (!entry) return;
    if (entry.type === "stroke") {
      const list = this.pages.get(entry.page) ?? [];
      this.pages.set(
        entry.page,
        list.filter((s) => s.id !== entry.id),
      );
    } else {
      this.pages.set(entry.page, entry.strokes);
    }
    this.touch(entry.page);
  }

  clearPage(page: number): void {
    const strokes = this.pages.get(page);
    if (!strokes?.length) return;
    this.pushUndo({ type: "clear", page, strokes });
    this.pages.set(page, []);
    this.touch(page);
  }

  private pushUndo(entry: UndoEntry): void {
    this.undoStack.push(entry);
    if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
    this.canUndo = true;
  }

  private touch(page: number): void {
    this.revisions[page] = (this.revisions[page] ?? 0) + 1;
    this.schedule(SAVE_DELAY);
  }

  // --- Speichern ------------------------------------------------------------

  private serialize(): StrokeDocumentInput {
    const pages: StrokeDocumentInput["pages"] = {};
    for (const [page, strokes] of this.pages) {
      if (strokes.length) pages[String(page)] = { strokes };
    }
    return { version: 2, pages };
  }

  private replace(document: StrokeDocument, version: number): void {
    this.pages.clear();
    for (const [key, page] of Object.entries(document.pages)) {
      this.pages.set(
        Number(key),
        page.strokes.map((s) => ({ ...s, points: s.points.map((p) => [...p] as Point) })),
      );
    }
    this.version = version;
    this.undoStack = [];
    this.canUndo = false;
    const revisions: Record<number, number> = {};
    for (const page of new Set([...Object.keys(this.revisions).map(Number), ...this.pages.keys()])) {
      revisions[page] = (this.revisions[page] ?? 0) + 1;
    }
    this.revisions = revisions;
  }

  private schedule(delay: number): void {
    if (this.disposed || !this.loaded) return;
    if (this.timer) clearTimeout(this.timer);
    // Ein Fehler bleibt sichtbar, bis wirklich gespeichert wurde.
    if (this.saving !== "error") this.saving = "pending";
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.save();
    }, delay);
  }

  async save(): Promise<void> {
    if (this.inflight) {
      await this.inflight;
      if (this.saving !== "pending") return;
    }
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.saving = "saving";
    this.inflight = this.persist().finally(() => (this.inflight = null));
    await this.inflight;
  }

  private async persist(): Promise<void> {
    try {
      const result = await api.saveAnnotations(this.documentId, this.serialize(), this.version);
      this.version = result.version;
      if (this.saving === "saving") this.saving = "idle";
    } catch (error) {
      if (error instanceof ApiError && error.code === "ANNOTATION_CONFLICT") {
        await this.mergeWithServer();
        return;
      }
      this.saving = "error";
      if (!this.disposed) this.schedule(RETRY_DELAY);
    }
  }

  /** Parallel auf einem anderen Gerät gespeichert: Striche beider Seiten vereinen. */
  private async mergeWithServer(): Promise<void> {
    const server = await api.annotations(this.documentId);
    // Kopie: replace() leert die bestehende Map
    const local = new Map(this.pages);
    this.replace(server.document, server.version);
    for (const [page, strokes] of local) {
      const known = new Set((this.pages.get(page) ?? []).map((s) => s.id));
      const extra = strokes.filter((s) => !known.has(s.id));
      if (extra.length) this.pages.set(page, [...(this.pages.get(page) ?? []), ...extra]);
      this.revisions[page] = (this.revisions[page] ?? 0) + 1;
    }
    ui.toast("Notizen mit dem anderen Gerät zusammengeführt");
    this.saving = "saving";
    await this.persist();
  }

  /** Beim Verlassen: ausstehende Änderungen noch senden (keepalive, falls klein genug). */
  flush(): void {
    if (this.saving === "idle" || !this.loaded) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const body = this.serialize();
    const small = JSON.stringify(body).length < KEEPALIVE_MAX_BYTES;
    void api.saveAnnotations(this.documentId, body, this.version, small).then(
      (result) => {
        this.version = result.version;
        this.saving = "idle";
      },
      () => undefined,
    );
  }

  dispose(): void {
    this.flush();
    this.disposed = true;
  }
}
